"use client";

import Image from "next/image";
import React, { useEffect, useState } from "react";

import { getAssetFileNameFromUrl, hasArrayElements, isPDF } from "@/apiUtils/commonUtils";
import { prescriptionSources } from "@/apiUtils/constants";
import CalenderIcon from "@/assets/icons/calender.svg?react";
import InStoreIcon from "@/assets/icons/in-store.svg?react";
import UserUploadIcon from "@/assets/icons/user-upload.svg?react";
import IconNoImage from "@/assets/images/no_image.svg";
import { PrescriptionData, TranslationData } from "@/common/lib/types";
import LazyImage from "@/components/lazyImage/LazyImage";
import ConfirmationModal from "@/components/lcp/components/ConfirmationModal";
import PrescriptionMeasurements from "@/components/lcp/phaseOne/prescription/saved/Measurements";
import ViewPrescriptionDocuments from "@/components/lcp/phaseOne/prescription/saved/ViewPrescriptionDocuments";
import Button from "@/components/ui/button/Button";
import Skeleton from "@/components/ui/skeleton/Skeleton";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";

interface IProps {
  className?: string;
  hideViewDocument?: boolean;
  translations: TranslationData;
  prescription: PrescriptionData;
  onDelete?: (id: string) => Promise<void> | void;
  onSelect?: (item: PrescriptionData) => Promise<void> | void;
  onEdit?: (prescription?: PrescriptionData) => Promise<void> | void;
}

const PrescriptionCard = ({
  onEdit,
  onDelete,
  onSelect,
  className,
  translations,
  prescription,
  hideViewDocument,
}: IProps) => {
  const [fileName, setFileName] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openImagesModal, setOpenImagesModal] = useState(false);
  const [fetchingFileName, setFetchingFileName] = useState(false);

  console.log(`Cloudinary Image Data - ${prescription?.title}`, {
    originalURl: prescription.images?.[0]?.url,
    cloudinaryURL: getCloudinaryURL(prescription.images?.[0]?.url ?? ""),
  });

  const { source, title, date, images, opticalData } = prescription;
  const [, extension] = fileName.split(".");
  const formattedFileName =
    fileName.length > 20 ? `${fileName?.substring(0, 20)}...${extension}` : fileName;

  const handleSelect = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    await onSelect?.(prescription);
  };

  const handleDelete = async () => {
    await onDelete?.(prescription.id);
    setOpenDeleteModal(false);
  };

  const handleEdit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    await onEdit?.(prescription);
  };

  const handleFetchFileName = async () => {
    try {
      if (!images || !hasArrayElements(images)) return;

      const url = images.at(0)?.url || "";
      let fName = getAssetFileNameFromUrl(url);
      if (fName) {
        setFileName(fName);
        return;
      }

      setFetchingFileName(true);
      const res = await fetch(url);

      setFileName(getAssetFileNameFromUrl(res.url));
      setFetchingFileName(false);
    } catch (error) {
      console.log(error);
      setFetchingFileName(false);
    }
  };

  useEffect(() => {
    handleFetchFileName();
  }, [images]);

  return (
    <div
      className={cn(
        "col-span-full md:col-span-6 lg:col-span-full min-[1120px]:col-span-6",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 bg-tertiary-5 px-4 py-3">
        <h2 title={title} className="truncate text-sm font-semibold">
          {title}
        </h2>
        <div className="flex gap-2">
          {onSelect && (
            <Button
              onClick={handleSelect}
              className="px-3 py-1 text-[10px] sm:px-3 sm:py-1 sm:text-[10px] md:px-3 md:py-1 md:text-[0.75rem]"
            >
              {translations?.lensConfigurator?.lens_config_select_button_label ?? "Select"}
            </Button>
          )}
          {onDelete && (
            <Button
              onClick={() => setOpenDeleteModal(true)}
              className="px-3 py-1 text-[10px] sm:px-3 sm:py-1 sm:text-[10px] md:px-3 md:py-1 md:text-[0.75rem]"
            >
              {translations?.lensConfigurator?.lens_config_delete_prescription_button_label ??
                "Delete"}
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between bg-primary-4 px-4 py-2">
        <div className="flex items-center gap-2">
          {source === prescriptionSources.USER_UPLOADED ? (
            <>
              <UserUploadIcon width={13} height={13} />
              <span className="text-[0.75rem] font-semibold">
                {translations?.lensConfigurator?.lens_config_user_upload_label}
              </span>
            </>
          ) : (
            <>
              <InStoreIcon />
              <span className="text-[0.75rem] font-semibold">
                {translations?.lensConfigurator?.lens_config_user_in_store_label}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CalenderIcon width={13} height={13} />
          <p className="text-sm">{date}</p>
        </div>
      </div>
      {opticalData ? (
        <PrescriptionMeasurements opticalData={opticalData} translations={translations} />
      ) : (
        <div className="flex items-center gap-2 bg-tertiary-5 py-4 pl-2 pr-4 md:gap-3 md:px-4">
          {images && hasArrayElements(images) ? (
            isPDF(images[0]?.url || "") ? (
              <p className="text-sm">PDF</p>
            ) : (
              <div className="h-[2.5rem] w-[2.5rem] flex-shrink-0 md:h-[3rem] md:w-[3.125rem]">
                <LazyImage
                  width={40}
                  height={40}
                  alt={images[0]?.name || ""}
                  className="h-full w-full rounded-full object-cover"
                  src={images[0]?.url ? getCloudinaryURL(images[0]?.url) : IconNoImage}
                />
              </div>
            )
          ) : (
            <div className="h-[2.5rem] w-[2.5rem] flex-shrink-0 md:h-[3rem] md:w-[3.125rem]">
              <Image
                width={40}
                height={40}
                src={IconNoImage}
                alt="No image icon"
                className="h-full w-full rounded-full object-cover"
              />
            </div>
          )}
          <div className="flex-1 text-sm">
            {fetchingFileName ? (
              <Skeleton className="mb-1 h-1 w-full bg-gray-200" />
            ) : (
              <p>{formattedFileName}</p>
            )}
            <div
              className={cn("flex items-center justify-between", hideViewDocument && "justify-end")}
            >
              {images && hasArrayElements(images) && images[0]?.url && !hideViewDocument && (
                <Button
                  variant="link"
                  className="font-normal"
                  onClick={() => setOpenImagesModal(true)}
                >
                  {translations?.lensConfigurator?.lens_config_view_document_link_label}
                </Button>
              )}
              {onEdit && (
                <Button variant="link" className="font-normal" onClick={handleEdit}>
                  {translations?.lensConfigurator?.lens_config_update_prescription_label ??
                    "Update"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {opticalData && images && hasArrayElements(images) && images[0]?.url && !hideViewDocument && (
        <Button
          variant="link"
          className="ml-3 mt-3 font-normal md:ml-4"
          onClick={() => setOpenImagesModal(true)}
        >
          {translations?.lensConfigurator?.lens_config_view_document_link_label}
        </Button>
      )}

      {/* Prescription documents modal */}
      {images && hasArrayElements(images) && (
        <ViewPrescriptionDocuments
          documents={images}
          open={openImagesModal}
          translations={translations}
          onClose={() => setOpenImagesModal(false)}
        />
      )}
      {/* Delete prescription modal */}
      <ConfirmationModal
        open={openDeleteModal}
        onConfirm={handleDelete}
        onCancel={() => setOpenDeleteModal(false)}
        slots={{
          cancel:
            translations?.lensConfigurator
              ?.lens_config_delete_prescription_overlay_cancel_button_label ?? "",
          confirm:
            translations?.lensConfigurator
              ?.lens_config_delete_prescription_overlay_confirm_button_label ?? "",
          title:
            translations?.lensConfigurator?.lens_config_delete_prescription_overlay_title ?? "",
        }}
      />
    </div>
  );
};

export default PrescriptionCard;
